// Observable-Based Predictions - No execution assumptions
// Pure market observation and book simulation

import type { OrderBookEntry } from '../types';
import type { BookAnalytics, TradePatterns } from './observableAnalytics';

// Enhanced prediction types
export interface ObservableFillProbability {
  probability: number; // 0-1 based on observable data
  confidence: 'low' | 'medium' | 'high';
  methodology: 'market_observation' | 'book_simulation' | 'pattern_analysis';
  dataPoints: number;
  factors: {
    queuePosition: number;
    levelPersistence: number; // How long price levels typically persist
    marketActivity: number; // Current market activity level
    bookTurnover: number; // How quickly the book turns over
  };
  estimatedWaitTime: number; // Based on observable turnover
  assumptions: string[]; // Explicit list of any assumptions made
}

export interface ObservableMarketImpact {
  directImpact: number; // Direct price impact from consuming book levels
  estimatedTemporaryImpact: number; // Based on observable patterns
  liquidityConsumed: number; // Actual liquidity that would be consumed
  levelsAffected: number; // Number of price levels affected
  remainingSize: number; // Size that couldn't be filled from current book
  confidence: 'low' | 'medium' | 'high';
  methodology: 'book_simulation';
  factors: {
    bookDepth: number;
    spreadWidth: number;
    priceImpactHistory: number; // Historical price impact per unit volume
  };
}

export interface MarketActivityMetrics {
  currentActivity: 'low' | 'medium' | 'high' | 'very_high';
  tradesPerMinute: number;
  avgTradeSize: number;
  bookUpdateFrequency: number;
  volumeVelocity: number; // Volume per unit time
  priceVelocity: number; // Price change per unit time
}

// Observable Fill Probability Calculator
export function calculateObservableFillProbability(
  orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
  side: 'buy' | 'sell',
  size: number,
  price: number,
  bookAnalytics: BookAnalytics,
  tradePatterns: TradePatterns
): ObservableFillProbability {

  const levels = side === 'buy' ? orderBook.asks : orderBook.bids;

  // Factor 1: Queue Position Analysis
  const queueAnalysis = analyzeQueuePosition(levels, side, size, price);

  // Factor 2: Level Persistence (how long price levels survive)
  const persistenceFactor = bookAnalytics.priceLevelUpdates.persistenceRate;

  // Factor 3: Market Activity Level
  const activityMetrics = calculateActivityMetrics(tradePatterns);

  // Factor 4: Book Turnover Rate
  const turnoverRate = calculateBookTurnoverRate(bookAnalytics, tradePatterns);

  // Calculate probability based on observable factors
  let baseProbability = 0;

  if (queueAnalysis.isImmediatelyFillable) {
    // Order can be filled immediately from current book
    baseProbability = 0.95;
  } else if (queueAnalysis.isInQueue) {
    // Order is at a price level that exists in the book
    baseProbability = persistenceFactor * 0.8;
  } else {
    // Order would create a new price level
    baseProbability = Math.max(0.1, persistenceFactor * 0.5);
  }

  // Adjust for market activity
  const activityMultiplier = calculateActivityMultiplier(activityMetrics);
  const finalProbability = Math.min(0.98, baseProbability * activityMultiplier);

  // Estimate wait time based on observable turnover
  const estimatedWaitTime = estimateWaitTime(queueAnalysis, turnoverRate, activityMetrics);

  // Determine confidence based on data quality
  const confidence = determineConfidence(bookAnalytics, tradePatterns);

  return {
    probability: finalProbability,
    confidence,
    methodology: queueAnalysis.isImmediatelyFillable ? 'book_simulation' : 'market_observation',
    dataPoints: tradePatterns.priceImpactObservations.observations.length,
    factors: {
      queuePosition: queueAnalysis.position,
      levelPersistence: persistenceFactor,
      marketActivity: activityMetrics.volumeVelocity,
      bookTurnover: turnoverRate
    },
    estimatedWaitTime,
    assumptions: buildAssumptionsList(queueAnalysis, activityMetrics)
  };
}

// Queue Position Analysis
function analyzeQueuePosition(
  levels: OrderBookEntry[],
  side: 'buy' | 'sell',
  size: number,
  price: number
): {
  position: number;
  isImmediatelyFillable: boolean;
  isInQueue: boolean;
  availableAtPrice: number;
  aheadInQueue: number;
} {
  let position = 0;
  let cumulativeSize = 0;
  let availableAtPrice = 0;
  let aheadInQueue = 0;
  let isImmediatelyFillable = false;
  let isInQueue = false;

  for (const level of levels) {
    position++;

    // Check if this order would be immediately fillable
    if ((side === 'buy' && level.price <= price) ||
        (side === 'sell' && level.price >= price)) {
      cumulativeSize += level.size;

      if (cumulativeSize >= size) {
        isImmediatelyFillable = true;
        break;
      }
    }

    // Check if order would join existing queue at this price
    if (level.price === price) {
      availableAtPrice = level.size;
      isInQueue = true;
      break;
    }

    // Count liquidity ahead in queue
    if ((side === 'buy' && level.price > price) ||
        (side === 'sell' && level.price < price)) {
      aheadInQueue += level.size;
    }
  }

  return {
    position,
    isImmediatelyFillable,
    isInQueue,
    availableAtPrice,
    aheadInQueue
  };
}

// Market Activity Metrics Calculator
function calculateActivityMetrics(tradePatterns: TradePatterns): MarketActivityMetrics {
  const tradesPerMinute = tradePatterns.timingPatterns.frequency;
  const avgTradeSize = tradePatterns.sizeDistribution.averageSize;

  // Estimate book update frequency from trade patterns
  const bookUpdateFrequency = tradesPerMinute * 1.5; // Assume ~1.5 book updates per trade

  const volumeVelocity = tradesPerMinute * avgTradeSize;

  // Calculate price velocity from impact observations
  const priceVelocity = tradePatterns.priceImpactObservations.observations.length > 0 ?
    tradePatterns.priceImpactObservations.observations
      .slice(-10) // Last 10 observations
      .reduce((sum, obs) => sum + Math.abs(obs.priceChangePercent), 0) / 10 : 0;

  let currentActivity: MarketActivityMetrics['currentActivity'] = 'low';
  if (tradesPerMinute > 5) currentActivity = 'very_high';
  else if (tradesPerMinute > 2) currentActivity = 'high';
  else if (tradesPerMinute > 0.5) currentActivity = 'medium';

  return {
    currentActivity,
    tradesPerMinute,
    avgTradeSize,
    bookUpdateFrequency,
    volumeVelocity,
    priceVelocity
  };
}

// Book Turnover Rate Calculator
function calculateBookTurnoverRate(bookAnalytics: BookAnalytics, _tradePatterns: TradePatterns): number {
  // Combine level persistence with trade frequency to estimate turnover
  const levelLifetime = bookAnalytics.priceLevelUpdates.averageLifetime; // seconds
  const updateFrequency = bookAnalytics.priceLevelUpdates.frequency; // updates per minute

  // Higher update frequency and shorter level lifetime = higher turnover
  const baseTurnover = updateFrequency / 60; // per second
  const lifetimeAdjustment = levelLifetime > 0 ? 60 / levelLifetime : 1;

  return baseTurnover * lifetimeAdjustment;
}

// Activity Multiplier Calculator
function calculateActivityMultiplier(activityMetrics: MarketActivityMetrics): number {
  switch (activityMetrics.currentActivity) {
    case 'very_high': return 1.3;
    case 'high': return 1.15;
    case 'medium': return 1.0;
    case 'low': return 0.7;
    default: return 1.0;
  }
}

// Wait Time Estimator
function estimateWaitTime(
  queueAnalysis: any,
  turnoverRate: number,
  activityMetrics: MarketActivityMetrics
): number {
  if (queueAnalysis.isImmediatelyFillable) {
    return 0; // Immediate fill
  }

  if (queueAnalysis.isInQueue) {
    // Estimate based on position in existing queue
    const baseWaitTime = queueAnalysis.position * 30; // 30 seconds per position
    const activityAdjustment = activityMetrics.currentActivity === 'high' ? 0.6 :
                              activityMetrics.currentActivity === 'medium' ? 0.8 : 1.2;
    return baseWaitTime * activityAdjustment;
  }

  // New price level - estimate based on market activity
  const baseWaitTime = turnoverRate > 0 ? 60 / turnoverRate : 120; // Base estimate
  const activityBonus = activityMetrics.tradesPerMinute > 1 ? 0.7 : 1.0;

  return Math.max(30, baseWaitTime * activityBonus);
}

// Confidence Determiner
function determineConfidence(
  bookAnalytics: BookAnalytics,
  tradePatterns: TradePatterns
): 'low' | 'medium' | 'high' {
  const tradeData = tradePatterns.priceImpactObservations.observations.length;
  const bookStability = bookAnalytics.spreadStability;
  const depthPersistence = bookAnalytics.depthPersistence;

  if (tradeData > 20 && bookStability > 0.7 && depthPersistence > 0.6) {
    return 'high';
  } else if (tradeData > 10 && bookStability > 0.5 && depthPersistence > 0.4) {
    return 'medium';
  } else {
    return 'low';
  }
}

// Build Assumptions List
function buildAssumptionsList(
  queueAnalysis: any,
  activityMetrics: MarketActivityMetrics
): string[] {
  const assumptions: string[] = [];

  if (!queueAnalysis.isImmediatelyFillable) {
    assumptions.push('Level persistence based on recent history');
    assumptions.push('Market activity remains similar to recent patterns');
  }

  if (activityMetrics.currentActivity === 'low') {
    assumptions.push('Low activity may reduce fill probability');
  }

  return assumptions;
}

// Observable Market Impact Calculator
export function calculateObservableMarketImpact(
  orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
  size: number,
  side: 'buy' | 'sell',
  tradePatterns: TradePatterns
): ObservableMarketImpact {
  const levels = side === 'buy' ? orderBook.asks : orderBook.bids;

  // Simulate consuming the order book
  const simulation = simulateBookConsumption(levels, size, side);

  // Get historical price impact data
  const historicalImpact = tradePatterns.priceImpactObservations.averageImpactPerVolume;

  // Estimate temporary impact (typically 60-80% of total impact reverses)
  const temporaryRatio = tradePatterns.priceImpactObservations.temporaryImpactRatio;
  const estimatedTemporaryImpact = simulation.priceImpact * temporaryRatio;

  // Determine confidence based on data quality
  const confidence = simulation.remainingSize === 0 ? 'high' : 'medium';

  return {
    directImpact: simulation.priceImpact,
    estimatedTemporaryImpact,
    liquidityConsumed: simulation.liquidityConsumed,
    levelsAffected: simulation.levelsConsumed,
    remainingSize: simulation.remainingSize,
    confidence,
    methodology: 'book_simulation',
    factors: {
      bookDepth: levels.slice(0, 10).reduce((sum, level) => sum + level.size, 0),
      spreadWidth: levels.length > 0 ? levels[levels.length - 1].price - levels[0].price : 0,
      priceImpactHistory: historicalImpact
    }
  };
}

// Book Consumption Simulator
function simulateBookConsumption(
  levels: OrderBookEntry[],
  size: number,
  _side: 'buy' | 'sell'
): {
  priceImpact: number;
  liquidityConsumed: number;
  levelsConsumed: number;
  remainingSize: number;
  weightedAveragePrice: number;
} {
  if (levels.length === 0) {
    return {
      priceImpact: 0,
      liquidityConsumed: 0,
      levelsConsumed: 0,
      remainingSize: size,
      weightedAveragePrice: 0
    };
  }

  let remainingSize = size;
  let totalValue = 0;
  let totalVolume = 0;
  let levelsConsumed = 0;
  const startPrice = levels[0].price;

  for (const level of levels) {
    if (remainingSize <= 0) break;

    const sizeAtLevel = Math.min(remainingSize, level.size);
    totalValue += level.price * sizeAtLevel;
    totalVolume += sizeAtLevel;
    remainingSize -= sizeAtLevel;
    levelsConsumed++;
  }

  const weightedAveragePrice = totalVolume > 0 ? totalValue / totalVolume : startPrice;
  const priceImpact = Math.abs(weightedAveragePrice - startPrice);

  return {
    priceImpact,
    liquidityConsumed: totalVolume,
    levelsConsumed,
    remainingSize,
    weightedAveragePrice
  };
}

// Market Activity Profiler
export function createMarketActivityProfile(
  bookAnalytics: BookAnalytics,
  tradePatterns: TradePatterns
): {
  currentProfile: 'dormant' | 'quiet' | 'active' | 'volatile';
  characteristics: {
    avgSpread: number;
    bookStability: number;
    tradeFrequency: number;
    volatilityLevel: number;
  };
  recommendations: {
    optimalOrderSize: number;
    suggestedStrategy: 'patient' | 'opportunistic' | 'aggressive';
    confidenceLevel: number;
  };
} {
  const activityMetrics = calculateActivityMetrics(tradePatterns);
  const spread = bookAnalytics.spreadStability;
  const recentObservations = tradePatterns.priceImpactObservations.observations.slice(-10);
  const volatility = recentObservations.length > 0 ?
    Math.sqrt(recentObservations.reduce((sum, obs) => sum + Math.pow(obs.priceChangePercent, 2), 0) / recentObservations.length) : 0;

  // Determine market profile
  let currentProfile: 'dormant' | 'quiet' | 'active' | 'volatile' = 'quiet';

  if (activityMetrics.tradesPerMinute < 0.1) {
    currentProfile = 'dormant';
  } else if (activityMetrics.tradesPerMinute > 3 || volatility > 0.02) {
    currentProfile = 'volatile';
  } else if (activityMetrics.tradesPerMinute > 1) {
    currentProfile = 'active';
  }

  // Generate recommendations based on profile
  const avgTradeSize = activityMetrics.avgTradeSize;
  let optimalOrderSize = Math.round(avgTradeSize * 0.7); // Conservative sizing
  let suggestedStrategy: 'patient' | 'opportunistic' | 'aggressive' = 'patient';

  switch (currentProfile) {
    case 'dormant':
      optimalOrderSize = Math.round(avgTradeSize * 0.3);
      suggestedStrategy = 'patient';
      break;
    case 'active':
      optimalOrderSize = Math.round(avgTradeSize * 0.8);
      suggestedStrategy = 'opportunistic';
      break;
    case 'volatile':
      optimalOrderSize = Math.round(avgTradeSize * 0.5);
      suggestedStrategy = 'aggressive';
      break;
  }

  const confidenceLevel = determineConfidence(bookAnalytics, tradePatterns) === 'high' ? 0.8 :
                         determineConfidence(bookAnalytics, tradePatterns) === 'medium' ? 0.6 : 0.4;

  return {
    currentProfile,
    characteristics: {
      avgSpread: spread,
      bookStability: bookAnalytics.depthPersistence,
      tradeFrequency: activityMetrics.tradesPerMinute,
      volatilityLevel: volatility
    },
    recommendations: {
      optimalOrderSize,
      suggestedStrategy,
      confidenceLevel
    }
  };
}